/**
 * JSON-LD Schema helpers for Technical SEO
 */

import { siteConfig } from '../config';

const siteUrl = siteConfig.siteUrl;
const siteName = siteConfig.siteName;

export interface Game {
  slug: string;
  title: string;
  description: string;
  thumbnail: string;
  playUrl: string;
  developer?: string;
  rating?: string;
  votes?: string;
  released?: string;
  publishedDate?: string;
  lastUpdated?: string;
  categories: string[];
  technology?: string;
  platforms?: string;
  howToPlay?: string;
  gameTips?: { title: string; tip: string }[];
}

/**
 * Organization & WebSite Schema for Homepage
 */
export function buildHomePageSchema() {
  const organization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteName,
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${siteUrl}/assets/img/logo.png`,
    },
    sameAs: [
      // Add social media profiles here if available
    ],
  };

  const website = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: siteUrl,
    inLanguage: 'en',
    description: siteConfig.siteDescription,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [organization, website],
  };
}

/**
 * VideoGame Schema for Game Pages
 */
export function buildGameSchema(game: Game, pageUrl?: string) {
  const aggregateRating = game.rating && game.votes ? {
    '@type': 'AggregateRating',
    ratingValue: game.rating,
    ratingCount: game.votes.replace(/,/g, ''),
    bestRating: '5',
    worstRating: '1',
  } : undefined;

  // Only use tracked dates — avoid new Date() so dateModified stays stable across builds
  const modifiedDate = game.lastUpdated || game.publishedDate || undefined;

  // Detect multiplayer from categories
  const MULTIPLAYER_CATEGORIES = ['multiplayer', 'io', '2-player'];
  const isMultiplayer = game.categories?.some(c =>
    MULTIPLAYER_CATEGORIES.includes(c.toLowerCase())
  );
  const playMode = isMultiplayer ? 'MultiPlayer' : 'SinglePlayer';

  // Derive operating system from technology/platforms fields
  const operatingSystem = game.platforms ?? (game.technology === 'HTML5' ? 'Web Browser' : 'Web Browser');

  return {
    '@context': 'https://schema.org',
    '@type': ['VideoGame', 'SoftwareApplication'],
    name: game.title,
    description: game.description,
    url: pageUrl || `${siteUrl}/${game.slug}`,
    image: `${siteUrl}${game.thumbnail}`,
    genre: game.categories,
    inLanguage: 'en',
    isAccessibleForFree: true,
    author: game.developer ? {
      '@type': 'Organization',
      name: game.developer,
    } : undefined,
    publisher: {
      '@type': 'Organization',
      name: siteName,
      url: siteUrl,
    },
    datePublished: game.publishedDate || game.released,
    dateModified: modifiedDate,
    applicationCategory: 'Game',
    operatingSystem,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    aggregateRating,
    gamePlatform: ['Web Browser', 'Desktop', 'Mobile'],
    playMode,
    numberOfPlayers: isMultiplayer ? {
      '@type': 'QuantitativeValue',
      minValue: 2,
    } : undefined,
  };
}

/**
 * BreadcrumbList Schema for Game Pages
 */
export function buildGameBreadcrumbSchema(game: Game, categoryName?: string, categorySlug?: string) {
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
  ];

  if (categoryName && categorySlug) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: categoryName,
      item: `${siteUrl}/category/${categorySlug}`,
    });
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: game.title,
      item: `${siteUrl}/${game.slug}`,
    });
  } else {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: game.title,
      item: `${siteUrl}/${game.slug}`,
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

/**
 * CollectionPage Schema for Category Pages
 */
export function buildCategorySchema(categoryName: string, categorySlug: string, description: string, gamesCount: number) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} Games`,
    description,
    url: `${siteUrl}/category/${categorySlug}`,
    mainEntity: {
      '@type': 'ItemList',
      name: `${categoryName} Games`,
      description,
      numberOfItems: gamesCount,
    },
  };
}

/**
 * BreadcrumbList Schema for Category Pages
 */
export function buildCategoryBreadcrumbSchema(categoryName: string, categorySlug: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: `${siteUrl}/category/${categorySlug}`,
      },
    ],
  };
}

/**
 * HowTo Schema for Game Pages
 * Converts the howToPlay paragraph into structured HowToStep items.
 * Each sentence becomes one step, enabling rich results in Search.
 */
export function buildHowToSchema(game: Game, pageUrl?: string) {
  if (!game.howToPlay) return null;

  // Split on sentence boundaries (. ! ?) followed by whitespace
  const rawSteps = game.howToPlay
    .split(/(?<=[.!?])\s+/)
    .map(s => s.trim())
    .filter(s => s.length > 15); // skip fragment-length sentences

  if (rawSteps.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to Play ${game.title}`,
    description: `Complete step-by-step guide to playing ${game.title} free in your browser.`,
    url: pageUrl || `${siteUrl}/${game.slug}`,
    step: rawSteps.map((text, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: text.split(/[—–,]/)[0].trim().slice(0, 60) || `Step ${i + 1}`,
      text,
    })),
  };
}

/**
 * FAQPage Schema for Games with FAQs
 */
export interface FAQItem {
  question: string;
  answer: string;
}

export function buildFAQSchema(faqs: FAQItem[]) {
  if (!faqs || faqs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * Combine multiple schemas into a @graph
 */
export function combineSchemas(...schemas: any[]) {
  return {
    '@context': 'https://schema.org',
    '@graph': schemas,
  };
}
